import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Resolve a post media value: data URLs / http URLs pass through; backend file paths get prefixed.
export const mediaSrc = (m) => {
  if (!m) return m;
  if (m.startsWith("/api/")) return `${BACKEND_URL}${m}`;
  return m;
};

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("hr_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  register: (body) => client.post("/auth/register", body).then((r) => r.data),
  login: (body) => client.post("/auth/login", body).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  bundle: () => client.get("/me").then((r) => r.data),
  updateProfile: (body) => client.put("/me/profile", body).then((r) => r.data),
  homeRangeProfile: () => client.get("/me/homerange-profile").then((r) => r.data),
  saveHomeRangeProfile: (body) => client.put("/me/homerange-profile", body).then((r) => r.data),
  clearPersonalization: () => client.post("/me/homerange-profile/clear-personalization").then((r) => r.data),

  products: (category) => client.get("/public/products", { params: { category } }).then((r) => r.data),
  priceHistory: (id) => client.get(`/public/products/${id}/price-history`).then((r) => r.data),
  reviews: (id) => client.get(`/public/products/${id}/reviews`).then((r) => r.data),
  huntListings: (params) => client.get("/public/hunt-listings", { params }).then((r) => r.data),
  huntListing: (id) => client.get(`/public/hunt-listings/${id}`).then((r) => r.data),
  inquiry: (body) => client.post("/hunt-listings/inquiry", body).then((r) => r.data),
  states: () => client.get("/public/official-resources/states").then((r) => r.data),
  officialResources: (jurisdiction) => client.get("/public/official-resources", { params: { jurisdiction } }).then((r) => r.data),
  stateDataHealth: (state) => client.get(`/public/states/${encodeURIComponent(state)}/data-health`).then((r) => r.data),
  trustTiers: () => client.get("/public/trust/tiers").then((r) => r.data),
  agencyOverview: () => client.get("/admin/official-resources/overview").then((r) => r.data),
  setAgencyPartner: (jurisdiction, official_partner) => client.post("/admin/official-resources/partner", { jurisdiction, official_partner }).then((r) => r.data),
  runLinkHealthCheck: () => client.post("/admin/official-resources/health-check").then((r) => r.data),
  setLinkAlertEmail: (email) => client.put("/admin/settings/link-alert-email", { email }).then((r) => r.data),
  drawOdds: (jurisdiction) => client.get("/public/draw-odds", { params: { jurisdiction } }).then((r) => r.data),

  // ---- Draw Odds Explorer ----
  exploreStates: () => client.get("/public/explore/states").then((r) => r.data),
  exploreSpecies: (state) => client.get(`/public/explore/${encodeURIComponent(state)}/species`).then((r) => r.data),
  exploreHunts: (state, params) => client.get(`/public/explore/${encodeURIComponent(state)}/hunts`, { params }).then((r) => r.data),
  plannerAnalyze: (body) => client.post("/public/planner/analyze", body).then((r) => r.data),
  specialDraws: (params) => client.get("/public/special-draws", { params }).then((r) => r.data),
  myDeadlines: () => client.get("/me/deadlines").then((r) => r.data),
  getDeadlineReminders: () => client.get("/me/deadline-reminders").then((r) => r.data),
  putDeadlineReminders: (body) => client.put("/me/deadline-reminders", body).then((r) => r.data),
  adminSpecialDraws: () => client.get("/admin/special-draws").then((r) => r.data),
  adminSpecialUpdate: (id, body) => client.put(`/admin/special-draws/${id}`, body).then((r) => r.data),
  adminSpecialDelete: (id) => client.delete(`/admin/special-draws/${id}`).then((r) => r.data),
  stateDrawProfile: (state) => client.get(`/public/states/${encodeURIComponent(state)}/draw-profile`).then((r) => r.data),
  exploreHuntDetail: (id, points) => client.get(`/public/explore/hunt/${id}`, { params: points != null ? { points } : {} }).then((r) => r.data),
  exploreStateOverview: (state) => client.get(`/public/explore/${encodeURIComponent(state)}/overview`).then((r) => r.data),

  // ---- Data Health (admin) ----
  adminDataHealth: () => client.get("/admin/data-health").then((r) => r.data),
  adminDiscrepancies: (status) => client.get("/admin/planner-discrepancies", { params: { status: status || "open" } }).then((r) => r.data),
  adminResolveDiscrepancy: (id, action) => client.post(`/admin/planner-discrepancies/${id}/action`, { action }).then((r) => r.data),
  adminDataAiRun: (body) => client.post("/admin/data-ai/run", body || {}).then((r) => r.data),
  adminReviewQueue: (params) => client.get("/admin/review-queue", { params }).then((r) => r.data),
  adminReviewAction: (id, body) => client.post(`/admin/review-queue/${id}/action`, body).then((r) => r.data),
  adminDataImport: (body) => client.post("/admin/data-import", body).then((r) => r.data),
  adminDataAiCollect: (body) => client.post("/admin/data-ai/collect", body).then((r) => r.data),
  cmsPages: (trash) => client.get("/admin/cms/pages", { params: { trash: !!trash } }).then((r) => r.data),
  cmsCreate: (body) => client.post("/admin/cms/pages", body).then((r) => r.data),
  cmsGet: (id) => client.get(`/admin/cms/pages/${id}`).then((r) => r.data),
  cmsUpdate: (id, body) => client.put(`/admin/cms/pages/${id}`, body).then((r) => r.data),
  cmsPublish: (id) => client.post(`/admin/cms/pages/${id}/publish`).then((r) => r.data),
  cmsUnpublish: (id) => client.post(`/admin/cms/pages/${id}/unpublish`).then((r) => r.data),
  cmsDuplicate: (id) => client.post(`/admin/cms/pages/${id}/duplicate`).then((r) => r.data),
  cmsTrash: (id) => client.delete(`/admin/cms/pages/${id}`).then((r) => r.data),
  cmsRestore: (id) => client.post(`/admin/cms/pages/${id}/restore`).then((r) => r.data),
  cmsDeleteForever: (id) => client.delete(`/admin/cms/pages/${id}/permanent`).then((r) => r.data),
  cmsRevisions: (id) => client.get(`/admin/cms/pages/${id}/revisions`).then((r) => r.data),
  cmsRestoreRevision: (id, revId) => client.post(`/admin/cms/pages/${id}/revisions/${revId}/restore`).then((r) => r.data),
  cmsPublic: (slug) => client.get(`/public/cms/pages/${slug}`).then((r) => r.data),
  cmsSchedule: (id, scheduled_at) => client.post(`/admin/cms/pages/${id}/schedule`, { scheduled_at }).then((r) => r.data),
  cmsUnschedule: (id) => client.post(`/admin/cms/pages/${id}/unschedule`).then((r) => r.data),

  // Media Library (reusable images & PDFs)
  mediaList: (kind) => client.get("/admin/media", { params: { kind } }).then((r) => r.data),
  mediaUpload: (file, title) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/admin/media", form, {
      headers: { "Content-Type": "multipart/form-data" }, params: { title },
    }).then((r) => r.data);
  },
  mediaDelete: (id) => client.delete(`/admin/media/${id}`).then((r) => r.data),

  // State Info documents
  adminStateDocs: (state) => client.get(`/admin/states/${encodeURIComponent(state)}/documents`).then((r) => r.data),
  adminStateDocCreate: (state, body) => client.post(`/admin/states/${encodeURIComponent(state)}/documents`, body).then((r) => r.data),
  adminStateDocUpdate: (state, id, body) => client.put(`/admin/states/${encodeURIComponent(state)}/documents/${id}`, body).then((r) => r.data),
  adminStateDocDelete: (state, id) => client.delete(`/admin/states/${encodeURIComponent(state)}/documents/${id}`).then((r) => r.data),
  adminSourceAiTest: (state, id, query) => client.post(`/admin/states/${encodeURIComponent(state)}/documents/${id}/ai-test`, { query }).then((r) => r.data),
  adminDocSupersede: (state, id, replaces_id) => client.post(`/admin/states/${encodeURIComponent(state)}/documents/${id}/supersede`, { replaces_id }).then((r) => r.data),
  publicStateDocs: (state) => client.get(`/public/states/${encodeURIComponent(state)}/documents`).then((r) => r.data),
  adminStateDocProcess: (state, id) => client.post(`/admin/states/${encodeURIComponent(state)}/documents/${id}/process`).then((r) => r.data),
  adminStateAiStatus: (state) => client.get(`/admin/states/${encodeURIComponent(state)}/ai-status`).then((r) => r.data),
  adminStateAiTest: (state, query) => client.post(`/admin/states/${encodeURIComponent(state)}/ai-test`, { query }).then((r) => r.data),
  adminReprocessAllKb: () => client.post(`/admin/ai-kb/reprocess-all`).then((r) => r.data),
  adminKbJob: () => client.get(`/admin/ai-kb/job`).then((r) => r.data),
  adminKbCoverage: () => client.get(`/admin/ai-kb/coverage`).then((r) => r.data),
  adminAiSettings: () => client.get(`/admin/ai-kb/settings`).then((r) => r.data),
  adminUpdateAiSettings: (body) => client.put(`/admin/ai-kb/settings`, body).then((r) => r.data),
  aiHubOverview: () => client.get(`/admin/ai-hub/overview`).then((r) => r.data),
  aiHubUsage: (days = 30) => client.get(`/admin/ai-hub/usage?days=${days}`).then((r) => r.data),
  aiHubHealth: () => client.get(`/admin/ai-hub/health`).then((r) => r.data),
  aiHubTrustedDomains: () => client.get(`/admin/ai-hub/trusted-domains`).then((r) => r.data),
  aiHubAddTrustedDomain: (body) => client.post(`/admin/ai-hub/trusted-domains`, body).then((r) => r.data),
  aiHubDeleteTrustedDomain: (id) => client.delete(`/admin/ai-hub/trusted-domains/${id}`).then((r) => r.data),
  aiHubDiscovered: (status = "new") => client.get(`/admin/ai-hub/discovered?status=${status}`).then((r) => r.data),
  aiHubDiscoveredAction: (id, body) => client.post(`/admin/ai-hub/discovered/${id}/action`, body).then((r) => r.data),
  aiHubAudit: () => client.get(`/admin/ai-hub/audit`).then((r) => r.data),

  // Merch (Printify)
  merchProducts: () => client.get("/public/merch/products").then((r) => r.data),
  merchProduct: (id) => client.get(`/public/merch/products/${id}`).then((r) => r.data),
  merchFeatured: () => client.get("/public/merch/featured").then((r) => r.data),
  merchShipping: (body) => client.post("/public/merch/shipping", body).then((r) => r.data),
  merchCheckout: (body) => client.post("/public/merch/checkout", body).then((r) => r.data),
  adminMerchStatus: () => client.get("/admin/merch/status").then((r) => r.data),
  adminMerchSync: () => client.post("/admin/merch/sync").then((r) => r.data),
  adminMerchProducts: () => client.get("/admin/merch/products").then((r) => r.data),
  adminMerchFeature: (id, featured) => client.post(`/admin/merch/products/${id}/feature`, { featured }).then((r) => r.data),
  adminMerchOrders: () => client.get("/admin/merch/orders").then((r) => r.data),

  // Commerce (native HomeRange Goods)
  shopCategories: () => client.get("/public/shop/categories").then((r) => r.data),
  shopProducts: (params) => client.get("/public/shop/products", { params }).then((r) => r.data),
  shopProduct: (slug) => client.get(`/public/shop/products/${slug}`).then((r) => r.data),
  shopFeatured: () => client.get("/public/shop/featured").then((r) => r.data),
  shopShipping: (body) => client.post("/public/shop/shipping", body).then((r) => r.data),
  shopCheckout: (body) => client.post("/public/shop/checkout", body).then((r) => r.data),
  admCommerceSyncStatus: () => client.get("/admin/commerce/printify-sync-status").then((r) => r.data),
  admCommerceOverview: () => client.get("/admin/commerce/overview").then((r) => r.data),
  admCommerceProducts: (params) => client.get("/admin/commerce/products", { params }).then((r) => r.data),
  admCommerceProduct: (id) => client.get(`/admin/commerce/products/${id}`).then((r) => r.data),
  admCommerceCreate: (body) => client.post("/admin/commerce/products", body || {}).then((r) => r.data),
  admCommerceUpdate: (id, body) => client.put(`/admin/commerce/products/${id}`, body).then((r) => r.data),
  admCommercePublish: (id, override) => client.post(`/admin/commerce/products/${id}/publish`, { override }).then((r) => r.data),
  admCommerceUnpublish: (id) => client.post(`/admin/commerce/products/${id}/unpublish`).then((r) => r.data),
  admCommercePullPrintifyImages: (id) => client.post(`/admin/commerce/products/${id}/pull-printify-images`).then((r) => r.data),
  admCommerceArchive: (id) => client.post(`/admin/commerce/products/${id}/archive`).then((r) => r.data),
  admCommerceReorder: (ids) => client.post("/admin/commerce/products/reorder", { ids }).then((r) => r.data),
  admHomepageGoods: () => client.get("/admin/commerce/homepage-goods").then((r) => r.data),
  admHomepageGoodsUpdate: (product_ids) => client.put("/admin/commerce/homepage-goods", { product_ids }).then((r) => r.data),
  admCommerceImport: () => client.post("/admin/commerce/import-printify").then((r) => r.data),
  admCommerceSync: () => client.post("/admin/commerce/sync-printify").then((r) => r.data),
  admCommerceSeedCats: () => client.post("/admin/commerce/seed-categories").then((r) => r.data),
  admCategories: () => client.get("/admin/commerce/categories").then((r) => r.data),
  admCategoryCreate: (body) => client.post("/admin/commerce/categories", body).then((r) => r.data),
  admCategoryUpdate: (id, body) => client.put(`/admin/commerce/categories/${id}`, body).then((r) => r.data),
  admCategoryDelete: (id) => client.delete(`/admin/commerce/categories/${id}`).then((r) => r.data),
  admCollections: () => client.get("/admin/commerce/collections").then((r) => r.data),
  admCollectionCreate: (body) => client.post("/admin/commerce/collections", body).then((r) => r.data),
  admCollectionDelete: (id) => client.delete(`/admin/commerce/collections/${id}`).then((r) => r.data),
  admDiscounts: () => client.get("/admin/commerce/discounts").then((r) => r.data),
  admDiscountCreate: (body) => client.post("/admin/commerce/discounts", body).then((r) => r.data),
  admDiscountToggle: (id) => client.post(`/admin/commerce/discounts/${id}/toggle`).then((r) => r.data),
  admDiscountDelete: (id) => client.delete(`/admin/commerce/discounts/${id}`).then((r) => r.data),
  admShippingSettings: () => client.get("/admin/commerce/shipping-settings").then((r) => r.data),
  admShippingSettingsUpdate: (body) => client.put("/admin/commerce/shipping-settings", body).then((r) => r.data),
  admOrders: (params) => client.get("/admin/commerce/orders", { params }).then((r) => r.data),
  admOrderDetail: (sid) => client.get(`/admin/commerce/orders/${sid}`).then((r) => r.data),
  admReviews: (params) => client.get("/admin/commerce/reviews", { params }).then((r) => r.data),
  admReviewModerate: (id, action) => client.post(`/admin/commerce/reviews/${id}/moderate`, { action }).then((r) => r.data),
  admReviewDelete: (id) => client.delete(`/admin/commerce/reviews/${id}`).then((r) => r.data),
  admReturns: (params) => client.get("/admin/commerce/returns", { params }).then((r) => r.data),
  admReturnAction: (id, action) => client.post(`/admin/commerce/returns/${id}/action`, { action }).then((r) => r.data),
  shopCreateReview: (pid, body) => client.post(`/shop/products/${pid}/reviews`, body).then((r) => r.data),
  shopRequestReturn: (body) => client.post("/public/shop/returns", body).then((r) => r.data),

  // User management
  adminUsers: (q, status) => client.get("/admin/users", { params: { q: q || undefined, status: status || undefined } }).then((r) => r.data),
  adminUserVerify: (id, verified) => client.post(`/admin/users/${id}/verify`, { verified }).then((r) => r.data),
  adminUserStatus: (id, status) => client.post(`/admin/users/${id}/status`, { status }).then((r) => r.data),

  // Archive moderation
  modListPosts: (status, q) => client.get("/admin/archive/posts", { params: { status: status || undefined, q: q || undefined } }).then((r) => r.data),
  modGetPost: (id) => client.get(`/admin/archive/posts/${id}`).then((r) => r.data),
  modHidePost: (id, hidden) => client.post(`/admin/archive/posts/${id}/hide`, { hidden }).then((r) => r.data),
  modDeletePost: (id) => client.delete(`/admin/archive/posts/${id}`).then((r) => r.data),
  modHideComment: (postId, commentId, hidden) => client.post(`/admin/archive/posts/${postId}/comments/${commentId}/hide`, { hidden }).then((r) => r.data),
  modDeleteComment: (postId, commentId) => client.delete(`/admin/archive/posts/${postId}/comments/${commentId}`).then((r) => r.data),
  adminImportTemplate: () => client.get("/admin/data-import/template", { responseType: "blob" }).then((r) => r.data),

  // ---- Points Planner (auth) ----
  myPoints: () => client.get("/me/points").then((r) => r.data),
  savePoints: (body) => client.put("/me/points", body).then((r) => r.data),
  deletePoints: (id) => client.delete(`/me/points/${id}`).then((r) => r.data),
  applications: () => client.get("/me/applications").then((r) => r.data),
  createApplication: (body) => client.post("/me/applications", body).then((r) => r.data),
  updateApplication: (id, body) => client.put(`/me/applications/${id}`, body).then((r) => r.data),
  deleteApplication: (id) => client.delete(`/me/applications/${id}`).then((r) => r.data),
  convertApplicationToHunt: (id) => client.post(`/me/applications/${id}/convert-to-hunt`).then((r) => r.data),
  trophyRoom: () => client.get("/me/trophy-room").then((r) => r.data),
  yearInReview: (year) => client.get(`/me/year-in-review/${year}`).then((r) => r.data),
  drawPlan: () => client.get("/me/draw-plan").then((r) => r.data),
  huntSummary: () => client.get("/me/hunt-summary").then((r) => r.data),
  passport: () => client.get("/me/passport").then((r) => r.data),
  aiContext: () => client.get("/me/ai-context").then((r) => r.data),
  followedStates: () => client.get("/me/followed-states").then((r) => r.data),
  toggleFollowState: (state) => client.post("/me/followed-states/toggle", { state }).then((r) => r.data),
  stateSummary: (state) => client.get(`/me/state-summary/${encodeURIComponent(state)}`).then((r) => r.data),

  businessTiers: () => client.get("/public/business-tiers").then((r) => r.data),
  recipes: (params) => client.get("/public/recipes", { params: typeof params === "string" ? { category: params } : (params || {}) }).then((r) => r.data),
  recipeFilters: (category) => client.get("/public/recipe-filters", { params: { category } }).then((r) => r.data),
  recipeVideos: () => client.get("/public/recipe-videos").then((r) => r.data),
  recipe: (id) => client.get(`/public/recipes/${id}`).then((r) => r.data),
  recipeSuggestions: () => client.get("/me/recipe-suggestions").then((r) => r.data),
  saveRecipe: (id) => client.post(`/recipes/${id}/save`).then((r) => r.data),
  savedRecipes: () => client.get("/me/saved-recipes").then((r) => r.data),
  savedRecipeIds: () => client.get("/me/saved-recipe-ids").then((r) => r.data),

  // ---- Partner Platform ----
  partnerMe: () => client.get("/partner/me").then((r) => r.data),
  partnerSetup: (body) => client.post("/partner/me/setup", body).then((r) => r.data),
  partnerAnalytics: () => client.get("/partner/me/analytics").then((r) => r.data),
  partnerAnalyticsTimeseries: (days) => client.get("/partner/me/analytics/timeseries", { params: { days } }).then((r) => r.data),
  partnerOnboarding: () => client.get("/partner/onboarding").then((r) => r.data),
  partnerOnboardingPublish: () => client.post("/partner/onboarding/publish").then((r) => r.data),
  adminProvisionPartner: (body) => client.post("/admin/partners/provision", body).then((r) => r.data),
  partnerUploadGallery: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/partner/me/gallery", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  partnerUploadOfferingImage: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/partner/me/offering-image", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  partnerCreateUpdate: (body) => client.post("/partner/me/updates", body).then((r) => r.data),
  partnerMyUpdates: (params) => client.get("/partner/me/updates", { params: params || {} }).then((r) => r.data),
  partnerEditUpdate: (id, body) => client.put(`/partner/me/updates/${id}`, body).then((r) => r.data),
  partnerDeleteUpdate: (id) => client.delete(`/partner/me/updates/${id}`).then((r) => r.data),
  partnerPublicUpdates: (bizId, params) => client.get(`/public/partner-updates/${bizId}`, { params: params || {} }).then((r) => r.data),
  partnerDirectory: (params) => client.get("/public/partners", { params: params || {} }).then((r) => r.data),
  partnerFeatured: (limit) => client.get("/public/partners/featured", { params: { limit } }).then((r) => r.data),
  partnerSuggest: (params) => client.get("/public/partners/suggest", { params: params || {} }).then((r) => r.data),
  recommendations: (params) => client.get("/public/recommendations", { params: params || {} }).then((r) => r.data),
  adminFeaturePartner: (id, featured) => client.post(`/admin/partners/${id}/feature`, { featured }).then((r) => r.data),
  statePartnerContent: (state) => client.get(`/public/states/${encodeURIComponent(state)}/partner`).then((r) => r.data),
  adminPartnerSystemHealth: () => client.get("/admin/partners/system-health").then((r) => r.data),
  partnerUploadUpdateImage: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/partner/me/update-image", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  adminPartnerUpdates: (params) => client.get("/admin/partner-updates", { params: params || {} }).then((r) => r.data),
  adminModeratePartnerUpdate: (id, hidden) => client.post(`/admin/partner-updates/${id}/moderate`, { hidden }).then((r) => r.data),
  publicPartner: (prefix, slug, params) => client.get(`/public/partners/${prefix}/${slug}`, { params: params || {} }).then((r) => r.data),
  partnerTrack: (bizId, body) => client.post(`/public/partners/${bizId}/track`, body).then((r) => r.data),
  referralResolve: (code, session_id) => client.get(`/r/${code}`, { params: { session_id } }).then((r) => r.data),
  adminPartners: (params) => client.get("/admin/partners", { params: params || {} }).then((r) => r.data),
  adminPartnersOverview: () => client.get("/admin/partners/overview").then((r) => r.data),
  adminPartnerAiIndex: () => client.get("/admin/partners/ai-index").then((r) => r.data),
  adminPartnerAiReindex: () => client.post("/admin/partners/ai-reindex").then((r) => r.data),
  adminPartnerSlug: (id, slug) => client.post(`/admin/partners/${id}/slug`, { slug }).then((r) => r.data),
  adminPartnerRegenReferral: (id) => client.post(`/admin/partners/${id}/regenerate-qr`).then((r) => r.data),
  recipePhotos: (id) => client.get(`/public/recipes/${id}/photos`).then((r) => r.data),
  addRecipePhoto: (id, body) => client.post(`/recipes/${id}/photos`, body).then((r) => r.data),
  recipeRatings: (id) => client.get(`/public/recipes/${id}/ratings`).then((r) => r.data),
  rateRecipe: (id, rating, review) => client.post(`/recipes/${id}/rate`, { rating, review }).then((r) => r.data),
  likePhoto: (id) => client.post(`/recipe-photos/${id}/like`).then((r) => r.data),
  commentPhoto: (id, text) => client.post(`/recipe-photos/${id}/comment`, { text }).then((r) => r.data),

  hunts: () => client.get("/hunts").then((r) => r.data),
  createHunt: (body) => client.post("/hunts", body).then((r) => r.data),
  updateHunt: (id, body) => client.patch(`/hunts/${id}`, body).then((r) => r.data),
  deleteHunt: (id) => client.delete(`/hunts/${id}`).then((r) => r.data),
  shareHunt: (id) => client.post(`/hunts/${id}/share`).then((r) => r.data),
  huntLog: (id) => client.get(`/hunts/${id}/log`).then((r) => r.data),
  updateHuntLog: (id, body) => client.put(`/hunts/${id}/log`, body).then((r) => r.data),
  uploadHuntPhoto: (id, file, caption) => {
    const form = new FormData();
    form.append("file", file);
    return client.post(`/hunts/${id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" }, params: { caption: caption || "" } }).then((r) => r.data);
  },
  deleteHuntPhoto: (id, photoId) => client.delete(`/hunts/${id}/photos/${photoId}`).then((r) => r.data),
  toggleHuntCard: (id, isPublic) => client.post(`/hunts/${id}/card`, { public: isPublic }).then((r) => r.data),
  publicHuntCard: (cardId) => client.get(`/public/hunt-cards/${cardId}`).then((r) => r.data),
  addPartyMember: (id, body) => client.post(`/hunts/${id}/party`, body).then((r) => r.data),
  removePartyMember: (id, memberId) => client.delete(`/hunts/${id}/party/${memberId}`).then((r) => r.data),
  huntChecklist: (id) => client.get(`/hunts/${id}/checklist`).then((r) => r.data),
  addTask: (id, title) => client.post(`/hunts/${id}/checklist`, { title }).then((r) => r.data),
  updateTask: (id, taskId, body) => client.put(`/hunts/${id}/checklist/${taskId}`, body).then((r) => r.data),
  deleteTask: (id, taskId) => client.delete(`/hunts/${id}/checklist/${taskId}`).then((r) => r.data),
  huntPacking: (id) => client.get(`/hunts/${id}/packing`).then((r) => r.data),
  addPacking: (id, body) => client.post(`/hunts/${id}/packing`, body).then((r) => r.data),
  updatePacking: (id, itemId, body) => client.put(`/hunts/${id}/packing/${itemId}`, body).then((r) => r.data),
  deletePacking: (id, itemId) => client.delete(`/hunts/${id}/packing/${itemId}`).then((r) => r.data),
  inviteHunter: (id, body) => client.post(`/hunts/${id}/invite`, body).then((r) => r.data),
  myHuntInvites: () => client.get("/me/hunt-invites").then((r) => r.data),
  respondInvite: (memberId, accept) => client.post(`/me/hunt-invites/${memberId}/respond`, { accept }).then((r) => r.data),
  huntCamp: (id) => client.get(`/hunts/${id}/camp`).then((r) => r.data),
  postCampMessage: (id, text) => client.post(`/hunts/${id}/camp/messages`, { text }).then((r) => r.data),
  updateCampNotes: (id, notes) => client.put(`/hunts/${id}/camp/notes`, { notes }).then((r) => r.data),
  sharedHunt: (shareId) => client.get(`/public/hunts/shared/${shareId}`).then((r) => r.data),
  gear: () => client.get("/gear-locker").then((r) => r.data),
  addGear: (body) => client.post("/gear-locker", body).then((r) => r.data),
  deleteGear: (id) => client.delete(`/gear-locker/${id}`).then((r) => r.data),
  gearCompatibility: (huntId) => client.get(`/me/hunts/${huntId}/gear-compatibility`).then((r) => r.data),
  huntFit: (huntId, productId) => client.get(`/me/hunts/${huntId}/products/${productId}/fit`).then((r) => r.data),
  huntConditions: (huntId) => client.get(`/me/hunts/${huntId}/conditions`).then((r) => r.data),

  saves: () => client.get("/me/saves").then((r) => r.data),
  checkSave: (type, ref) => client.get(`/me/saves/check/${type}/${ref}`).then((r) => r.data),
  toggleSave: (body) => client.post("/me/saves/toggle", body).then((r) => r.data),

  askAI: (body, personalized) =>
    client.post(personalized ? "/me/ai/ask" : "/public/ai/ask", body).then((r) => r.data),
  streamAI: async (body, personalized, { onMeta, onDelta, onDone, onError } = {}) => {
    const token = localStorage.getItem("hr_token");
    const res = await fetch(`${API}${personalized ? "/me/ai/ask/stream" : "/public/ai/ask/stream"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    if (!res.ok || !res.body) throw new Error("stream failed");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        let ev;
        try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
        if (ev.type === "meta") onMeta?.(ev);
        else if (ev.type === "delta") onDelta?.(ev.content || "");
        else if (ev.type === "done") onDone?.(ev);
        else if (ev.type === "error") onError?.(ev);
      }
    }
  },
  aiHistory: () => client.get("/me/ai/history").then((r) => r.data),
  aiConversations: () => client.get("/me/ai/conversations").then((r) => r.data),
  aiConversation: (id) => client.get(`/me/ai/conversations/${id}`).then((r) => r.data),
  deleteAiConversation: (id) => client.delete(`/me/ai/conversations/${id}`).then((r) => r.data),
  gearReadiness: () => client.get("/me/gear-readiness").then((r) => r.data),
  conditions: (state) => client.get(`/public/conditions/${encodeURIComponent(state)}`).then((r) => r.data),

  // FIELD AI — Product Intelligence
  fieldCategories: () => client.get("/field/categories").then((r) => r.data),
  fieldProducts: (params) => client.get("/field/products", { params }).then((r) => r.data),
  fieldProduct: (id, huntId) => client.get(`/field/products/${id}`, { params: { hunt_id: huntId } }).then((r) => r.data),
  fieldRecommend: (body) => client.post("/field/recommend", body).then((r) => r.data),
  fieldCompare: (body) => client.post("/field/compare", body).then((r) => r.data),
  fieldGearBuilder: (body) => client.post("/field/gear-builder", body).then((r) => r.data),
  fieldAsk: (body) => client.post("/field/ask", body).then((r) => r.data),
  fieldAdminOverview: () => client.get("/field/admin/overview").then((r) => r.data),
  fieldAdminRefresh: () => client.post("/field/admin/refresh").then((r) => r.data),

  // Marketplace + Business
  marketplacePlans: () => client.get("/marketplace/plans").then((r) => r.data),
  marketplaceCategories: () => client.get("/marketplace/categories").then((r) => r.data),
  createBusiness: (body) => client.post("/business", body).then((r) => r.data),
  myBusiness: () => client.get("/business/me").then((r) => r.data),
  updateBusiness: (body) => client.put("/business/me", body).then((r) => r.data),
  uploadBusinessCover: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/business/me/cover", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  uploadBusinessLogo: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/business/me/logo", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  createListing: (body) => client.post("/business/me/listings", body).then((r) => r.data),
  createListingsBulk: (listings) => client.post("/business/me/listings/bulk", { listings }).then((r) => r.data),
  uploadListingImages: (files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    return client.post("/business/me/listing-images", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  updateListing: (id, body) => client.put(`/business/me/listings/${id}`, body).then((r) => r.data),
  setListingStatus: (id, status) => client.post(`/business/me/listings/${id}/status?status=${status}`).then((r) => r.data),
  myBusinessOrders: () => client.get("/business/me/orders").then((r) => r.data),
  markOrderShipped: (txnId, body) => client.post(`/business/me/orders/${txnId}/ship`, body).then((r) => r.data),
  updateStock: (id, body) => client.patch(`/business/me/listings/${id}/stock`, body).then((r) => r.data),
  myBusinessAnalytics: () => client.get("/business/me/analytics").then((r) => r.data),
  pinListing: (id) => client.post(`/business/me/listings/${id}/pin`).then((r) => r.data),
  trackStorefrontView: (id) => client.post(`/marketplace/businesses/${id}/view`).then((r) => r.data).catch(() => {}),
  trackStorefrontClick: (id, target) => client.post(`/marketplace/businesses/${id}/click?target=${encodeURIComponent(target)}`).then((r) => r.data).catch(() => {}),
  connectOnboard: (originUrl) => client.post(`/business/connect/onboard?origin_url=${encodeURIComponent(originUrl)}`).then((r) => r.data),
  connectStatus: () => client.get("/business/connect/status").then((r) => r.data),
  subscribe: (body) => client.post("/marketplace/subscribe", body).then((r) => r.data),
  marketplaceListings: (params) => client.get("/marketplace/listings", { params }).then((r) => r.data),
  listingDetail: (id) => client.get(`/marketplace/listings/${id}`).then((r) => r.data),
  storefront: (id) => client.get(`/marketplace/businesses/${id}`).then((r) => r.data),
  followBusiness: (id) => client.post(`/marketplace/businesses/${id}/follow`).then((r) => r.data),
  marketplaceBusinesses: (params) => client.get("/marketplace/businesses", { params }).then((r) => r.data),
  marketplaceCheckout: (body) => client.post("/marketplace/checkout", body).then((r) => r.data),
  myOrders: () => client.get("/me/orders").then((r) => r.data),
  toggleWishlist: (id) => client.post(`/me/wishlist/${id}`).then((r) => r.data),
  myWishlist: () => client.get("/me/wishlist").then((r) => r.data),
  paymentStatus: (sessionId) => client.get(`/payments/status/${sessionId}`).then((r) => r.data),
  marketplaceAdminOverview: () => client.get("/marketplace/admin/overview").then((r) => r.data),
  verifyBusiness: (id, status) => client.post(`/business/${id}/verify`, { status }).then((r) => r.data),
  refundOrder: (txnId) => client.post(`/marketplace/transactions/${txnId}/refund`).then((r) => r.data),
  promoteListing: (id, originUrl) => client.post(`/business/me/listings/${id}/promote?origin_url=${encodeURIComponent(originUrl)}`).then((r) => r.data),
  updateMarketplaceConfig: (plans) => client.put("/marketplace/admin/config", { plans }).then((r) => r.data),
  getReviews: (params) => client.get("/marketplace/reviews", { params }).then((r) => r.data),
  reviewEligibility: (businessId) => client.get("/marketplace/review-eligibility", { params: { business_id: businessId } }).then((r) => r.data),
  createReview: (body) => client.post("/marketplace/reviews", body).then((r) => r.data),
  replyReview: (id, reply) => client.post(`/marketplace/reviews/${id}/reply`, { reply }).then((r) => r.data),
  moderateReview: (id, hidden) => client.post(`/marketplace/reviews/${id}/moderate`, { hidden }).then((r) => r.data),
  reviewCategories: () => client.get("/marketplace/review-categories").then((r) => r.data),

  journal: () => client.get("/journal").then((r) => r.data),
  createJournal: (body) => client.post("/journal", body).then((r) => r.data),
  updateJournal: (id, body) => client.patch(`/journal/${id}`, body).then((r) => r.data),
  deleteJournal: (id) => client.delete(`/journal/${id}`).then((r) => r.data),
  publicJournal: () => client.get("/journal/public").then((r) => r.data),

  // ---- Firearms Research ----
  publicFirearms: (params) => client.get("/public/firearms", { params }).then((r) => r.data),
  publicFirearm: (id) => client.get(`/public/firearms/${id}`).then((r) => r.data),
  adminFirearms: (params) => client.get("/admin/firearms", { params }).then((r) => r.data),
  adminFirearmCreate: (body) => client.post("/admin/firearms", body).then((r) => r.data),
  adminFirearmUpdate: (id, body) => client.patch(`/admin/firearms/${id}`, body).then((r) => r.data),
  adminFirearmDelete: (id) => client.delete(`/admin/firearms/${id}`).then((r) => r.data),

  adminInquiries: () => client.get("/admin/inquiries").then((r) => r.data),
  replyInquiry: (id, reply) => client.post(`/admin/inquiries/${id}/reply`, { reply }).then((r) => r.data),
  myInquiries: () => client.get("/me/inquiries").then((r) => r.data),
  inquiryMessage: (id, text) => client.post(`/me/inquiries/${id}/message`, { text }).then((r) => r.data),
  alerts: () => client.get("/me/alerts").then((r) => r.data),
  notifications: () => client.get("/me/notifications").then((r) => r.data),
  markNotificationsRead: (ids) => client.post("/me/notifications/read", { ids }).then((r) => r.data),
  alertDigest: () => client.post("/me/alerts/digest").then((r) => r.data),

  posts: (params) => client.get("/public/posts", { params }).then((r) => r.data),
  archiveOverview: () => client.get("/public/archive/overview").then((r) => r.data),
  searchUsers: (q) => client.get("/public/users", { params: { q } }).then((r) => r.data),
  trendingSpecies: () => client.get("/public/trending-species").then((r) => r.data),
  followingFeed: () => client.get("/me/feed").then((r) => r.data),
  myFollowing: () => client.get("/me/following").then((r) => r.data),
  toggleFollow: (id) => client.post(`/users/${id}/follow`).then((r) => r.data),
  hunterProfile: (id) => client.get(`/public/hunters/${id}`).then((r) => r.data),
  replyComment: (postId, commentId, text) => client.post(`/posts/${postId}/comments/${commentId}/reply`, { text }).then((r) => r.data),
  likeComment: (postId, commentId) => client.post(`/posts/${postId}/comments/${commentId}/like`).then((r) => r.data),
  notificationsUnread: () => client.get("/me/notifications/unread-count").then((r) => r.data),
  notificationsRead: () => client.post("/me/notifications/read").then((r) => r.data),
  uploadPostVideo: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/posts/video", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    }).then((r) => r.data);
  },
  createPost: (body) => client.post("/posts", body).then((r) => r.data),
  deletePost: (id) => client.delete(`/posts/${id}`).then((r) => r.data),
  likePost: (id) => client.post(`/posts/${id}/like`).then((r) => r.data),
  commentPost: (id, text) => client.post(`/posts/${id}/comment`, { text }).then((r) => r.data),

  // Advertising Marketplace
  adProducts: () => client.get("/advertising/products").then((r) => r.data),
  adStateAvailability: () => client.get("/advertising/state-availability").then((r) => r.data),
  adCheckout: (body) => client.post("/advertising/checkout", body).then((r) => r.data),
  adUpload: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/advertising/upload", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  myAdvertising: () => client.get("/me/advertising").then((r) => r.data),
  adSetup: (id, body) => client.post(`/advertising/${id}/setup`, body).then((r) => r.data),
  partnershipInquiry: (body) => client.post("/advertising/partnership-inquiry", body).then((r) => r.data),
  adminAdvertising: () => client.get("/admin/advertising").then((r) => r.data),
  adminSetAdStatus: (id, body) => client.post(`/admin/advertising/${id}/status`, body).then((r) => r.data),
  adminAdProducts: () => client.get("/admin/advertising/products").then((r) => r.data),
  adminUpdateAdProduct: (key, body) => client.put(`/admin/advertising/products/${key}`, body).then((r) => r.data),

  // ---- Admin Portal ----
  adminMe: () => client.get("/admin/me").then((r) => r.data),
  adminDashboard: (params) => client.get("/admin/dashboard", { params }).then((r) => r.data),
  adminRevenue: (params) => client.get("/admin/revenue", { params }).then((r) => r.data),
  adminSales: (params) => client.get("/admin/sales", { params }).then((r) => r.data),
  adminSaleDetail: (id) => client.get(`/admin/sales/${id}`).then((r) => r.data),
  adminRefund: (id) => client.post(`/admin/sales/${id}/refund`).then((r) => r.data),
  adminSalesExport: (params) => client.get("/admin/sales/export", { params, responseType: "blob" }).then((r) => r.data),
  adminFees: () => client.get("/admin/fees").then((r) => r.data),
  adminUpdateFees: (body) => client.put("/admin/fees", body).then((r) => r.data),
  adminSearch: (q) => client.get("/admin/search", { params: { q } }).then((r) => r.data),
  adminAuditLogs: (params) => client.get("/admin/audit-logs", { params }).then((r) => r.data),
  adminNotifications: () => client.get("/admin/notifications").then((r) => r.data),
  adminNotificationsReadAll: () => client.post("/admin/notifications/read-all").then((r) => r.data),
  adminAdmins: () => client.get("/admin/admins").then((r) => r.data),
  adminAddAdmin: (body) => client.post("/admin/admins", body).then((r) => r.data),
  adminSetAdminRoles: (id, roles) => client.put(`/admin/admins/${id}/roles`, { roles }).then((r) => r.data),
  adminRevokeAdmin: (id) => client.delete(`/admin/admins/${id}`).then((r) => r.data),

  // ---- Admin Phase 3 ----
  adminBusinesses: (params) => client.get("/admin/businesses", { params }).then((r) => r.data),
  adminBusinessDetail: (id) => client.get(`/admin/businesses/${id}`).then((r) => r.data),
  adminBusinessAction: (id, body) => client.post(`/admin/businesses/${id}/action`, body).then((r) => r.data),
  adminBusinessNote: (id, text) => client.post(`/admin/businesses/${id}/notes`, { text }).then((r) => r.data),
  adminApprovals: () => client.get("/admin/approvals").then((r) => r.data),
  adminGuides: () => client.get("/admin/guides").then((r) => r.data),
  adminSubscriptions: () => client.get("/admin/subscriptions").then((r) => r.data),
  adminSubscriptionAction: (id, body) => client.post(`/admin/subscriptions/${id}/action`, body).then((r) => r.data),
  adminPlans: () => client.get("/admin/plans").then((r) => r.data),
  adminUpdatePlan: (key, body) => client.put(`/admin/plans/${key}`, body).then((r) => r.data),

  // ---- Admin Phase 4: catalog ----
  adminListings: (params) => client.get("/admin/listings", { params }).then((r) => r.data),
  adminListingAction: (id, body) => client.post(`/admin/listings/${id}/action`, body).then((r) => r.data),
  adminListingEdit: (id, body) => client.put(`/admin/listings/${id}`, body).then((r) => r.data),
  adminRecipes: (params) => client.get("/admin/recipes", { params }).then((r) => r.data),
  adminRecipeCreate: (body) => client.post("/admin/recipes", body).then((r) => r.data),
  adminRecipeUpdate: (id, body) => client.put(`/admin/recipes/${id}`, body).then((r) => r.data),
  adminRecipeDelete: (id) => client.delete(`/admin/recipes/${id}`).then((r) => r.data),
  adminRecipeVideos: () => client.get("/admin/recipe-videos").then((r) => r.data),
  adminRecipeVideoCreate: (body) => client.post("/admin/recipe-videos", body).then((r) => r.data),
  adminRecipeVideoUpdate: (id, body) => client.put(`/admin/recipe-videos/${id}`, body).then((r) => r.data),
  adminRecipeVideoDelete: (id) => client.delete(`/admin/recipe-videos/${id}`).then((r) => r.data),
  adminHunts: (params) => client.get("/admin/hunts", { params }).then((r) => r.data),
  adminHuntCreate: (body) => client.post("/admin/hunts", body).then((r) => r.data),
  adminHuntUpdate: (id, body) => client.put(`/admin/hunts/${id}`, body).then((r) => r.data),
  adminHuntDelete: (id) => client.delete(`/admin/hunts/${id}`).then((r) => r.data),

  // ---- States pipeline ----
  adminStates: (params) => client.get("/admin/states", { params }).then((r) => r.data),
  adminStateUpdate: (state, body) => client.put(`/admin/states/${encodeURIComponent(state)}`, body).then((r) => r.data),
  adminStatesExport: () => client.get("/admin/states/export", { responseType: "blob" }).then((r) => r.data),

  // ---- Website / Homepage content ----
  siteContent: () => client.get("/public/site-content").then((r) => r.data),
  featured: () => client.get("/public/featured").then((r) => r.data),
  adminSiteContent: () => client.get("/admin/site-content").then((r) => r.data),
  adminSiteContentUpdate: (body) => client.put("/admin/site-content", body).then((r) => r.data),
  adminWebsiteOptions: () => client.get("/admin/website/options").then((r) => r.data),

  // ---- Account (public) ----
  forgotPassword: (body) => client.post("/auth/forgot-password", body).then((r) => r.data),
  resetPassword: (body) => client.post("/auth/reset-password", body).then((r) => r.data),
};

export default client;
